import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLocalStorageStore } from '../stores/localStorageStore';
import { userStore } from '../stores/userStore';
import useCountry from './useCountry';

const AUTH_ROUTES = [
  '/',
  '/login',
  '/signup',
  '/signupdetails',
  '/accountDetails',
  '/onboarding',
  '/verify-phone',
];

const usePhoneVerification = (isLoggedIn) => {
  const [showPhoneVerifyModal, setShowPhoneVerifyModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const userDataStore = userStore((state) => state.userData);
  const { isIndia: isIndianUser, isFetchingCountry } = useCountry();

  const { phoneVerifyModalShownTimestamp, setPhoneVerifyModalShownTimestamp } =
    useLocalStorageStore();

  const handleVerifyNow = () => {
    setShowPhoneVerifyModal(false);
    navigate('/verify-phone');
  };

  useEffect(() => {
    const posthogInstance = window.posthogWebview || window.posthog;

    const fetchProviderAndVerifyNumber = async (targetDate) => {
      try {
        const apiUrl = `${process.env.REACT_APP_BASE_URL}/users/${userDataStore.user}/account-provider/`;
        const res = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const data = await res.json();

        if (data.provider === 'google' || data.provider === 'email') {
          const noPhone = !userDataStore.phone || userDataStore.phone === '';

          if (noPhone) {
            if (new Date() >= targetDate) {
              navigate('/verify-phone');
              return;
            }
            setShowPhoneVerifyModal(true);
            setPhoneVerifyModalShownTimestamp(new Date().getTime().toString());
          } else {
            setShowPhoneVerifyModal(false);
          }
        }
      } catch (error) {
        console.error('Error fetching auth provider:', error);
      }
    };

    const showVerifyModal = async () => {
      const isFeatureEnabled = await posthogInstance?.isFeatureEnabled(
        'google-auth-expiry-date'
      );

      if (!isFeatureEnabled) {
        return;
      }

      const featureExpiryDate = await posthogInstance?.getFeatureFlagPayload(
        'google-auth-expiry-date'
      );
      const modalShownTimestampFromStore =
        useLocalStorageStore.getState().phoneVerifyModalShownTimestamp;
      const oneDayAgo = new Date().getTime() - 24 * 60 * 60 * 1000;
      const targetDate = new Date(featureExpiryDate.date).getTime();

      if (
        modalShownTimestampFromStore &&
        parseInt(modalShownTimestampFromStore) > oneDayAgo
      ) {
        return;
      }

      const onVerifyPhonePage = AUTH_ROUTES.includes(location.pathname);

      if (isLoggedIn && userDataStore.user && !onVerifyPhonePage) {
        setPhoneVerifyModalShownTimestamp(new Date().getTime().toString());
        fetchProviderAndVerifyNumber(targetDate);
      }
    };

    if (isIndianUser && !isFetchingCountry) {
      showVerifyModal();
    }
  }, [
    isLoggedIn,
    userDataStore.user,
    location.pathname,
    setPhoneVerifyModalShownTimestamp,
    navigate,
    isIndianUser,
    isFetchingCountry,
  ]);

  return { showPhoneVerifyModal, setShowPhoneVerifyModal, handleVerifyNow };
};

export default usePhoneVerification;
